import React, { useState, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { constructData, CommonStyles, GetConsolidatedLog } from './ConsolidatedController';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType3, colorType5, logStyles } from '../LogControllers/LogStyles';
import processData from '../common/service/dataProcessor';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '../../Common/Loader';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

export default function ConsolWasteLog(props: Props) {
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateLogCharts
      wasteLogData={reportData.items}
      wasteLogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateLogChartsProps {
  wasteLogData: any;
  wasteLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({
  wasteLogData, wasteLogColumns, siteId, logTitle, clientAPIURL, isDashboard, packageFilter,
}) => {
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = wasteLogColumns && [
    ...wasteLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  // Memoize processData output — prevents LogDateRangeFilter's useEffect([data]) firing every render
  const updatedData = useMemo(() => {
    const raw = processData(logTitle, wasteLogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, wasteLogData, packageFilter]);

  const [filteredData, setFilteredData] = useState<any[]>(updatedData ?? []);

  async function handleDataUpdate(item: any) {
    if (!item.ID) return;
    try {
      const accessToken = await getAccessToken();
      const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
      const rawItem = result?.items?.[0];
      if (!rawItem) return;
      const formatDate = (v: any): string => {
        if (!v) return v;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const d = adj.getDate(), m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
      };
      const updatedItem = { ...rawItem, Date: formatDate(rawItem.Date) };
      setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), updatedItem]);
    } catch (ex) {
      console.error(ex);
    }
  }

  const chartData = useMemo(() => {
    // Quantity-filtered subset — charts only include records with a recorded quantity
    const quantityData = filteredData.filter(
      (item: any) => item.QuantityTonne !== null && item.QuantityTonne !== '',
    );
    const uniquePeriod = [...new Set(quantityData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const uniqueDisposalMethods = [...new Set(quantityData.map((item: any) => item.WasteDisposalMethod))].filter(Boolean) as string[];
    const uniqueWasteTypes = [...new Set(quantityData.map((item: any) => item.WasteType))].filter(Boolean) as string[];

    const colorScaleLine = scaleOrdinal(schemeCategory10);

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineChartDataByMethod: Record<string, number[]> = {};
    uniqueDisposalMethods.forEach(m => { lineChartDataByMethod[m] = []; });
    uniquePeriod.forEach(period => {
      uniqueDisposalMethods.forEach(method => {
        const sum = quantityData
          .filter((item: any) => item.Period === period && item.WasteDisposalMethod === method)
          .reduce((acc: number, item: any) => acc + (Number(item.QuantityTonne) || 0), 0);
        lineChartDataByMethod[method].push(sum);
      });
    });
    const datasetsMethod = Object.entries(lineChartDataByMethod).map(([, values], idx) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLine(idx.toString()),
    }));
    const lineDataByMethod = { labels: uniquePeriod, datasets: datasetsMethod };
    const legendByMethod = uniqueDisposalMethods.map(name => ({ name }));

    const lineChartDataByType: Record<string, number[]> = {};
    uniqueWasteTypes.forEach(t => { lineChartDataByType[t] = []; });
    uniquePeriod.forEach(period => {
      uniqueWasteTypes.forEach(t => {
        const sum = quantityData
          .filter((item: any) => item.Period === period && item.WasteType === t)
          .reduce((acc: number, item: any) => acc + (Number(item.QuantityTonne) || 0), 0);
        lineChartDataByType[t].push(sum);
      });
    });
    const datasetsType = Object.entries(lineChartDataByType).map(([, values], idx) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLine(idx.toString()),
    }));
    const lineDataByType = { labels: uniquePeriod, datasets: datasetsType };
    const legendByType = uniqueWasteTypes.map(name => ({ name }));

    // PieChartComp contract: data[].name=string(non-null), data[].value=number, data[].color=string
    // Pies sum QuantityTonne (not counts)
    const sumByKey = (arr: any[], key: string): Record<string, number> => {
      const acc: Record<string, number> = {};
      arr.forEach((item: any) => {
        const k = item[key];
        if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
          acc[String(k)] = (acc[String(k)] ?? 0) + (Number(item.QuantityTonne) || 0);
        }
      });
      return acc;
    };
    const toPieData = (totals: Record<string, number>, colorFn: (k: string) => string) =>
      Object.entries(totals).map(([name, value], idx) => ({
        name,
        value: Math.round(value),
        color: colorFn(idx.toString()),
      }));

    const byWasteCategoryPieData = toPieData(sumByKey(filteredData, 'WasteCategory'), k => colorType5(k));
    const byWasteDisposalMethodPieData = toPieData(sumByKey(filteredData, 'WasteDisposalMethod'), k => colorType5(k));
    const byDivertedPieData = toPieData(sumByKey(filteredData, 'Diverted'), k => colorType5(k));
    const byTypeNonHazardousPieData = toPieData(
      sumByKey(filteredData.filter((i: any) => i.Hazardous === 'Non Hazardous'), 'WasteType'), k => colorType5(k),
    );
    const byTypeHazardousPieData = toPieData(
      sumByKey(filteredData.filter((i: any) => i.Hazardous === 'Hazardous'), 'WasteType'), k => colorType3(k),
    );

    // KPI summary — WasteCategory === 'Hazardous' for hazardous; Non Haz = Total − Hazardous
    const totalQty = Math.round(filteredData.reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0));
    const hazQty = parseFloat(
      filteredData.filter((i: any) => i.WasteCategory === 'Hazardous')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0).toFixed(2),
    );
    const nonHazQty = parseFloat((totalQty - hazQty).toFixed(2));
    const divertedQty = Math.round(
      filteredData.filter((i: any) => i.Diverted === 'Diverted')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0),
    );
    const notDivertedQty = Math.round(
      filteredData.filter((i: any) => i.Diverted === 'Not Diverted')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0),
    );
    const kpiItems = [
      { label: 'Total Qty (T)',    value: totalQty,        color: '#00695C' },
      { label: 'Entries',          value: filteredData.length, color: '#4527A0' },
      { label: 'Hazardous (T)',    value: hazQty,          color: '#C62828' },
      { label: 'Non Haz (T)',      value: nonHazQty,       color: '#2E7D32' },
      { label: 'Diverted (T)',     value: divertedQty,     color: '#1565C0' },
      { label: 'Not Diverted (T)', value: notDivertedQty,  color: '#F57C00' },
    ];

    return {
      kpiItems,
      lineDataByMethod, legendByMethod,
      lineDataByType, legendByType,
      byWasteCategoryPieData, byWasteDisposalMethodPieData,
      byDivertedPieData, byTypeNonHazardousPieData, byTypeHazardousPieData,
    };
  }, [filteredData]);

  const {
    kpiItems,
    lineDataByMethod, legendByMethod,
    lineDataByType, legendByType,
    byWasteCategoryPieData, byWasteDisposalMethodPieData,
    byDivertedPieData, byTypeNonHazardousPieData, byTypeHazardousPieData,
  } = chartData;

  const charts = (
    <View style={CommonStyles.container}>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byWasteCategoryPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={'Waste Qty / Category'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <LineChartScreen legend={legendByType} data={lineDataByType} label={lineDataByType.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={'Waste Qty / Category'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byWasteDisposalMethodPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={'Waste Qty / Destination'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <LineChartScreen legend={legendByMethod} data={lineDataByMethod} label={lineDataByMethod.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={'Waste Qty / Destination'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byTypeNonHazardousPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Non Hazardous Waste Qty / Type'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byTypeHazardousPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Hazardous Waste Qty / Type'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byDivertedPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Waste Diversion'} />
      </View>
    </View>
  );

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={updatedData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      {isDashboard ? (
        <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
          {kpiRow}
          <View>{charts}</View>
        </ScrollView>
      ) : (
        <TabsProvider defaultIndex={0}>
          <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>
            <TabScreen label="Charts" icon="chart-bar">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                {kpiRow}
                <View>{charts}</View>
              </ScrollView>
            </TabScreen>

            <TabScreen label="List" icon="database">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={CommonStyles.cardThree}>
                      {filteredData.length > 0 && ColumnsWithIDColumn && (
                        <PaperTableDynamic
                          data={filteredData}
                          columns={ColumnsWithIDColumn}
                          siteId={siteId}
                          logTitle={logTitle}
                          updateData={handleDataUpdate}
                          isConsolidatedTable={true}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
          </Tabs>
        </TabsProvider>
      )}
    </View>
  );
};
